/**
 * OfflineSyncIndicator
 *
 * Reusable sync status indicator for offline-capable modules.
 * Shows connectivity state and pending sync count with click-to-sync.
 */

import React from 'react';
import StatusDot from '@/components/common/StatusDot';
import type { StatusDotColor } from '@/components/common/StatusDot';

interface OfflineSyncIndicatorProps {
  /** Whether the device is currently offline */
  isOffline: boolean;
  /** Number of unsynced changes pending */
  unsyncedCount: number;
  /** Whether a sync operation is in progress */
  isSyncing?: boolean;
  /** Callback to trigger manual sync */
  onSync?: () => void;
  /** Optional additional CSS classes */
  className?: string;
}

const OfflineSyncIndicator: React.FC<OfflineSyncIndicatorProps> = ({
  isOffline,
  unsyncedCount,
  isSyncing = false,
  onSync,
  className = '',
}) => {
  let color: StatusDotColor;
  let label: string;

  if (isOffline) {
    color = 'red';
    label = 'Offline';
  } else if (unsyncedCount > 0) {
    color = 'yellow';
    label = `${unsyncedCount} pending`;
  } else {
    color = 'green';
    label = 'Synced';
  }

  if (isSyncing) {
    label = 'Syncing...';
  }

  const handleClick = () => {
    if (onSync && !isOffline && !isSyncing) {
      onSync();
    }
  };

  const isClickable = !!onSync && !isOffline && !isSyncing;

  return (
    <span
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
      className={`inline-flex items-center gap-1.5 text-xs text-theme-secondary ${
        isClickable ? 'cursor-pointer hover:text-gray-800 dark:hover:text-gray-200' : ''
      } ${className}`}
      title={isClickable ? 'Click to sync now' : label}
      aria-label={`Sync status: ${label}`}
    >
      <StatusDot
        color={color}
        size="sm"
        pulse={isSyncing || (isOffline && unsyncedCount > 0)}
      />
      <span>{label}</span>
    </span>
  );
};

export default OfflineSyncIndicator;
